import React, { useState } from 'react';
import { useI18n } from '../../hooks/useI18n';
import { useAuth } from '../../hooks/useAuth';
import { login as apiLogin } from '../../api/authApi';

interface LoginFormProps {
  onRegisterClick: () => void;
  onPhoneClick: () => void;
  onResetClick: () => void;
}

export const LoginForm: React.FC<LoginFormProps> = ({ onRegisterClick, onPhoneClick, onResetClick }) => {
  const { t } = useI18n();
  const { login } = useAuth();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isLoading) return;
    
    const trimmedEmail = email.trim();
    if (!trimmedEmail || !password) {
      setError(t('err_login_invalid') || 'Nieprawidłowy adres e-mail lub hasło');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const data = await apiLogin(trimmedEmail, password);
      login(data.token, data.refreshToken);
    } catch (err: any) {
      setError(t('err_login_invalid') || 'Nieprawidłowy adres e-mail lub hasło');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="cab-login-container">
      <div className="cab-back-btn-wrapper">
        <button type="button" className="cab-back-btn" onClick={onPhoneClick}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6"></polyline>
          </svg>
          {t('go_back')}
        </button>
      </div>
      
      <div className="cab-auth-form cab-login-form">
        <div className="cab-auth-header-wrapper">
        <h2>{t('login_title')}</h2>
        <p className="cab-sub">{t('login_sub_email')}</p>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="cab-field">
          <input 
            type="email" 
            className={`cab-input ${error ? 'has-error' : ''}`} 
            placeholder={t('email_placeholder') || "E-mail*"}
            aria-label={t('email')}
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (error) setError('');
            }}
            disabled={isLoading}
          />
        </div>
        
        <div className="cab-field cab-password-field">
          <input 
            type={showPassword ? "text" : "password"} 
            className={`cab-input ${error ? 'has-error' : ''}`} 
            placeholder={t('password_placeholder') || "Hasło*"}
            aria-label={t('password')}
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              if (error) setError('');
            }}
            disabled={isLoading}
          />
          <button 
            type="button" 
            className="cab-eye-btn" 
            onClick={() => setShowPassword(!showPassword)}
            tabIndex={-1}
            aria-label={t('show_hide_password') || "Pokaż/ukryj hasło"}
          >
            {showPassword ? (
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#A49BB6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                <circle cx="12" cy="12" r="3"></circle>
              </svg>
            ) : (
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#A49BB6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                <line x1="1" y1="1" x2="23" y2="23"></line>
              </svg>
            )}
          </button>
        </div>

        {error && <div className="cab-error-msg">{error}</div>}

        <button 
          type="submit" 
          className="cab-btn-primary"
          disabled={isLoading}
        >
          {isLoading ? '...' : t('login_btn')}
        </button>
      </form>

      <div className="cab-forgot-wrapper">
       
          <div className="cab-error-fallback-links">
             <span className="cab-forgot-link" onClick={onResetClick}>
              {t('forgot')}
            </span>
             <br />
            <span className="cab-forgot-link" onClick={onPhoneClick}>
              {t('login_by_phone')}
            </span>
           
           
          </div>
       
      </div>

      <style>{`
        .cab-login-form * {
          box-sizing: border-box;
        }
        .cab-login-form.cab-auth-form {
          background: #F8F6FC;
          border-radius: 32px;
          padding: 48px 32px;
          width: 100%;
          max-width: 644px;
          margin: 0 auto;
          text-align: center;
          font-family: inherit;
          margin-top: 50px;
        }
        .cab-auth-header-wrapper {
          margin-bottom: 24px;
        }
        .cab-login-form h2 {
          font-size: 26px;
          font-weight: 800;
          color: #361777;
          margin-bottom: 12px;
        }
        .cab-sub {
          font-size: 16px;
          color: #5A4E7A;
          line-height: 1.4;
          font-weight: 500;
          margin: 0;
        }
        .cab-field {
          margin-bottom: 16px;
          position: relative;
        }
        .cab-input {
          width: 100%;
          height: 56px;
          border-radius: 100px;
          background: #FFFFFF;
          border: 1px solid #FFFFFF;
          box-shadow: 0 4px 16px rgba(45, 21, 97, 0.03);
          padding: 0 24px;
          font-size: 16px;
          color: #361777;
          font-family: inherit;
          font-weight: 500;
          transition: border-color 0.2s;
        }
        .cab-input.has-error {
          border-color: #FF5A5F;
        }
        .cab-input:focus {
          outline: none;
          border-color: #361777;
        }
        .cab-input.has-error:focus {
          border-color: #FF5A5F;
        }
        .cab-input::placeholder {
          color: #A49BB6;
          font-weight: 500;
        }
        .cab-password-field .cab-input {
          padding-right: 54px;
        }
        .cab-eye-btn {
          position: absolute;
          right: 20px;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          padding: 0;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .cab-btn-primary {
          width: 100%;
          height: 60px;
          border-radius: 100px;
          background: #361777;
          color: #fff;
          font-size: 16px;
          font-weight: 600;
          border: none;
          cursor: pointer;
          transition: opacity 0.2s;
          margin-left: auto;
          margin-right: auto;
          margin-top: 8px;
          margin-bottom: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-family: inherit;
          max-width: 296px;
        }
        .cab-btn-primary:hover {
          opacity: 0.9;
        }
        .cab-btn-primary:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .cab-forgot-wrapper {
          margin-bottom: 0px;
        }
        .cab-error-fallback-links {
          line-height: 1.6;
        }
        .cab-forgot-link {
          color: #A49BB6;
          font-size: 16px;
          font-weight: 600;
          text-decoration: underline;
          cursor: pointer;
          transition: color 0.2s;
        }
        .cab-forgot-link:hover {
          color: #361777;
        }
        .cab-btn-outline {
          width: 100%;
          height: 60px;
          border-radius: 100px;
          background: transparent;
          color: #361777;
          font-size: 16px;
          font-weight: 700;
          border: 1px solid #361777;
          cursor: pointer;
          transition: background 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .cab-btn-outline:hover {
          background: rgba(45, 21, 97, 0.04);
        }
        .cab-error-msg {
          color: #FF5A5F;
          font-size: 14px;
          margin-bottom: 16px;
          font-weight: 500;
          text-align: center;
        }
        .cab-login-container {
          width: 100%;
          max-width: 1360px;
          margin: 0 auto;
        }
        .cab-back-btn-wrapper {
          display: flex;
          justify-content: flex-start;
          margin-bottom: 20px;
          padding-left: 48px;
        }
        .cab-back-btn {
          background: none;
          border: none;
          padding: 0;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 12px;
          color: #A49BB6;
          font-size: 20px;
          font-weight: 500;
          font-family: inherit;
          transition: color 0.2s;
        }
        .cab-back-btn:hover {
          color: #361777;
        }
        @media (max-width: 780px) {
          .cab-login-form.cab-auth-form {
            padding: 24px;
            border-radius: 24px;
          }
          .cab-back-btn-wrapper {
            margin-bottom: 16px;
            justify-content: flex-start;
            padding-left: 0;
          }
            .cab-back-btn {
            font-size:16px;
            gap:5px;
            white-space: nowrap;
          }
        }
      `}</style>
      </div>
    </div>
  );
};
